// The O-ring instrument (DESIGN.md "signature devices" #1): a stroke arc,
// stroke-linecap round, filling to a value over a dim track, on the ring's
// own geometry — an Archivo number stamped in the center. At 100% the arc
// closes into the complete circle.
//
// One SVG builder for every "how covered / how complete" hero on the admin
// panel. Job Costs (spend vs budget) and Contract (earned vs contract) both
// call this instead of keeping their own copies of the same shape — DESIGN.md
// is explicit that this device gets reused "with restraint, one per surface",
// and two copies of the geometry would have let the two rings quietly drift
// out of sync with each other over time.
//
// Pure string builder: no DOM, no Supabase, no i18n lookups — every piece of
// text (center value, caption, aria label) arrives already resolved from the
// caller, which is the one that knows its own money formatting and copy.

/**
 * @param {object} o
 * @param {number}  [o.size=132]     Outer SVG size in px (square).
 * @param {number}  [o.sw=11]        Stroke width in px.
 * @param {boolean} o.hasValue       False renders the "no denominator" state
 *                                   used before a budget/contract exists: a
 *                                   dim, empty track (drawn in --accent-ring)
 *                                   and a plain readout in the center instead
 *                                   of a percent arc.
 * @param {number}  [o.pct]          0..Infinity, ignored when hasValue is
 *                                   false. A value past 100 still closes the
 *                                   arc at a full circle — the overrun is
 *                                   reported in the center number, never by
 *                                   winding the stroke around a second lap.
 * @param {'accent'|'error'|'success'} [o.tone='accent']
 *                                   Arc + big-number color. Job Costs passes
 *                                   'error' when spend has passed the budget
 *                                   (high = bad, DESIGN.md: red is danger).
 *                                   Contract passes 'success' at >=100%
 *                                   (high = good there) and must NEVER pass
 *                                   'error' for being high — see 68-jobvalue.js
 *                                   for the comment on that inversion.
 * @param {string}  o.centerValue    Big Archivo text in the middle, already
 *                                   formatted (e.g. "62%" or a money string).
 * @param {string}  o.centerCap      Small caption under the big text.
 * @param {string}  o.ariaLabel      Plain text (not pre-escaped) for the SVG.
 */
export function ringSvg(o) {
  const size = o.size || 132, sw = o.sw || 11, r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const has = !!o.hasValue;
  const pctClamped = has ? Math.min(Math.max(0, Number(o.pct) || 0), 100) : 0;
  const arc = c * pctClamped / 100;
  const tone = o.tone || 'accent';
  // These arcs are STROKES, not fills — the raw semantic colors read fine on
  // the obsidian world this ring was designed for but fail the 3:1 non-text
  // UI floor against a light card: --accent #F89010 ~1.56:1, --success
  // #22c55e ~2.28:1. --error (#ef4444, fixed/non-themed) already clears the
  // floor at ~3.76:1, so it's left as plain --error rather than --error-ink
  // — dark's error-ink resolves to --error-bright (#f87171), a DIFFERENT
  // shade this ring never used, and swapping would regress the dark
  // baseline. accent-ink/success-ink fix light (6.38:1 / 5.02:1) and need
  // no dark pin: both -ink tokens' dark values already resolve back to the
  // plain var this ring originally used (T2 light sweep, 2026-08-05).
  const strokeColor = tone === 'error' ? 'var(--error)' : tone === 'success' ? 'var(--success-ink)' : 'var(--accent-ink)';
  const valClass = 'jc-ring-val' +
    (tone === 'error' ? ' jc-ring-val--over' : tone === 'success' ? ' jc-ring-val--complete' : '');

  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="${escAttr(o.ariaLabel || '')}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none"
              stroke="${has ? 'var(--surface-3)' : 'var(--accent-ring)'}" stroke-width="${sw}"/>
      ${has ? `
      <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none"
              stroke="${strokeColor}" stroke-width="${sw}"
              stroke-linecap="round"
              stroke-dasharray="${c.toFixed(1)} ${c.toFixed(1)}"
              stroke-dashoffset="${(c - arc).toFixed(1)}"
              transform="rotate(-90 ${size / 2} ${size / 2})"/>` : ''}
      <text x="50%" y="${has ? '46%' : '47%'}" text-anchor="middle" dominant-baseline="middle"
            class="${has ? valClass : 'jc-ring-money'}">${escText(o.centerValue)}</text>
      <text x="50%" y="63%" text-anchor="middle" dominant-baseline="middle"
            class="jc-ring-cap">${escText(o.centerCap)}</text>
    </svg>`;
}

function escAttr(t) {
  return String(t).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
function escText(t) {
  return String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
